import { type ISODateTimeWithUTCType } from '@principle-theorem/shared';
import {
  type IAIReceptionistKnowledgeBase,
  type IAIReceptionistSectionQaPairs,
} from '../ai-receptionist-knowledge-base';
import { type IWebsiteSource } from './website-snapshot';

/**
 * A value proposed for the Knowledge Base, paired with its Provenance (the
 * snapshot page URL it was read from) so the practice can check it before
 * accepting. Provenance is shown in the review only and is never saved onto
 * the Knowledge Base.
 */
export interface IImportProposedValue<T> {
  value: T;
  provenance: string;
}

export interface IImportProposedQaPair {
  question: string;
  answer: string;
  provenance: string;
  /**
   * The id of the existing pair, in the same section, that this pair would
   * update: accepting it replaces that pair's answer and keeps its id and
   * question. The import sets it only when it names a pair sent in the
   * request's `existingContent`; the review then links it to any pair of the
   * Knowledge Base asking the same question, including pairs the request left
   * out. Without it the pair is proposed as a new one.
   */
  existingId?: string;
}

export interface IImportProposedCustomSection {
  title: string;
  content: string;
  qa?: IImportProposedQaPair[];
  provenance: string;
  /**
   * The position of the existing custom section this one would update:
   * accepting it replaces that section's content and keeps its title and Q&A
   * pairs. The import sets it only when it names a section sent in the
   * request's `existingContent`; the review then links it to any section of
   * the Knowledge Base with the same title, including sections the request
   * left out. Without it the section is proposed as a new one.
   */
  existingIndex?: number;
}

/**
 * The Knowledge Base prose fields a Website Import may propose. A Website
 * Import only fills the editor's Practice Details, Our Team and Appointments &
 * Policies sections and Additional Information, plus Common Questions and
 * Custom Sections. `publicHolidayNote` and `afterHoursMessage` (Opening Hours)
 * are deliberately absent: they tell the receptionist how to handle calls,
 * which depends on its own setup rather than on what the website says.
 * `emergencyContact` and `emergencyHours` are absent too: a Website Import
 * never proposes phone numbers or emergency details, so the practice enters
 * them itself.
 * `generalServices`, `cosmeticServices` and `feesPayments` are absent too:
 * they are Derived Fields overwritten at push time, so a proposed value would
 * be silently discarded.
 */
export const IMPORT_PROPOSAL_PROSE_FIELDS = [
  'practiceDescription',
  'parkingAccess',
  'appointmentsBooking',
  'cancellationPolicy',
  'reschedulingPolicy',
  'ourTeam',
  'additionalInfo',
] as const satisfies readonly (keyof IAIReceptionistKnowledgeBase)[];

export type ImportProposalProseField =
  (typeof IMPORT_PROPOSAL_PROSE_FIELDS)[number];

export interface IImportProposalFields
  extends Partial<
    Record<ImportProposalProseField, IImportProposedValue<string>>
  > {
  allowsCancellations?: IImportProposedValue<boolean>;
  allowsRescheduling?: IImportProposedValue<boolean>;
  cancellationFeeWindowHours?: IImportProposedValue<number>;
  reschedulingWindowHours?: IImportProposedValue<number>;
}

/**
 * The yes-or-no policy switches a Website Import may propose. The extractor
 * and the proposal rules share this list: clamping keeps only the fields it
 * lists, so a switch missing from a separate copy would be read from the
 * website and then silently dropped.
 */
export const IMPORT_PROPOSAL_BOOLEAN_FIELDS = [
  'allowsCancellations',
  'allowsRescheduling',
] as const satisfies readonly (keyof IImportProposalFields)[];

export type ImportProposalBooleanField =
  (typeof IMPORT_PROPOSAL_BOOLEAN_FIELDS)[number];

/**
 * The notice windows, in hours, a Website Import may propose. Shared by the
 * extractor and the proposal rules for the same reason as
 * `IMPORT_PROPOSAL_BOOLEAN_FIELDS`.
 */
export const IMPORT_PROPOSAL_WINDOW_HOURS_FIELDS = [
  'cancellationFeeWindowHours',
  'reschedulingWindowHours',
] as const satisfies readonly (keyof IImportProposalFields)[];

export type ImportProposalWindowHoursField =
  (typeof IMPORT_PROPOSAL_WINDOW_HOURS_FIELDS)[number];

/**
 * The Knowledge Base sections a Website Import may propose Q&A pairs for, in
 * the order of the editor's Common Questions categories. The extractor, the
 * proposal rules and the review share this list, so every section is
 * extracted, kept and shown in the order the practice already knows.
 */
export const IMPORT_PROPOSAL_SECTION_QA_PAIR_KEYS = [
  'generalServices',
  'cosmeticServices',
  'appointments',
  'feesPayments',
  'parkingAccess',
  'ourTeam',
] as const satisfies readonly (keyof IAIReceptionistSectionQaPairs)[];

/**
 * Q&A pairs listed by Knowledge Base section, the shape shared by an Import
 * Proposal, a Website Import request and what the practice accepted.
 */
export type SectionQaPairLists<Pair> = Partial<
  Record<keyof IAIReceptionistSectionQaPairs, Pair[]>
>;

/**
 * Everything one Website Import proposes. It is reviewed by the practice and
 * never written anywhere by itself; accepted values reach the Knowledge Base
 * only through the editor's existing Save.
 */
export interface IImportProposal {
  source: IWebsiteSource;
  /**
   * The resolved root page URL from the snapshot. It differs from
   * `source.url` when the website redirects, so it is what the review shows
   * as the site that was read.
   */
  rootUrl: string;
  snapshotFetchedAt: ISODateTimeWithUTCType;
  pagesRequested: number;
  pagesRead: number;
  fields: IImportProposalFields;
  sectionQaPairs: SectionQaPairLists<IImportProposedQaPair>;
  customSections: IImportProposedCustomSection[];
}

export const IMPORT_PROPOSAL_MAX_QA_PER_SECTION = 5;
export const IMPORT_PROPOSAL_MAX_CUSTOM_SECTIONS = 5;
